import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faArrowDown,
  faPlus,
  faSave,
  faSpinner,
  faTrash,
  faUndo,
} from '@fortawesome/free-solid-svg-icons';

import getDraftDetails from '../../../../data/getDraftDetails';
import { setDraftDetails } from '../../../../data/setDraftDetails';
import { toastError, toastSuccess } from '../../../../helpers/toastHelpers';

const emptyLink = () => ({ description: '', link: '', icon: 'football' });

const iconOptions = [
  { value: 'football', label: 'Football' },
  { value: 'money', label: 'Money' },
  { value: 'cashApp', label: 'Cash App' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'slack', label: 'Slack' },
  { value: 'video', label: 'Video' },
];

const normalizeLink = (link) => ({
  description: typeof link?.description === 'string' ? link.description : '',
  link: typeof link?.link === 'string' ? link.link : '',
  icon: link?.icon || 'football',
});

const DraftSettingsManager = () => {
  const [draftDate, setDraftDate] = useState('');
  const [draftInfoDue, setDraftInfoDue] = useState('');
  const [draftLinks, setDraftLinks] = useState([emptyLink()]);
  const [savedState, setSavedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const result = await getDraftDetails();

    if (!result.success) {
      toastError('Unable to load draft settings');
      setLoading(false);
      return;
    }

    const nextLinks = Array.isArray(result.draftLinks) && result.draftLinks.length > 0
      ? result.draftLinks.map(normalizeLink)
      : [emptyLink()];

    const nextSavedState = {
      draftDate: result.draftDate || '',
      draftInfoDue: result.draftInfoDue || '',
      draftLinks: nextLinks.map((link) => ({ ...link })),
    };

    setDraftDate(nextSavedState.draftDate);
    setDraftInfoDue(nextSavedState.draftInfoDue);
    setDraftLinks(nextSavedState.draftLinks);
    setSavedState(nextSavedState);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateLink = (index, field, value) => {
    setDraftLinks((currentLinks) => currentLinks.map((link, linkIndex) => {
      if (linkIndex !== index) return link;
      return { ...link, [field]: value };
    }));
  };

  const addDraftLink = () => {
    setDraftLinks((currentLinks) => [...currentLinks, emptyLink()]);
  };

  const removeDraftLink = (index) => {
    setDraftLinks((currentLinks) => {
      if (currentLinks.length === 1) {
        return [emptyLink()];
      }

      return currentLinks.filter((_, linkIndex) => linkIndex !== index);
    });
  };

  const moveDraftLink = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= draftLinks.length || fromIndex === toIndex) return;

    setDraftLinks((currentLinks) => {
      const nextLinks = [...currentLinks];
      const [movedLink] = nextLinks.splice(fromIndex, 1);
      nextLinks.splice(toIndex, 0, movedLink);
      return nextLinks;
    });
  };

  const resetDraftSettings = () => {
    if (!savedState) return;

    setDraftDate(savedState.draftDate);
    setDraftInfoDue(savedState.draftInfoDue);
    setDraftLinks(savedState.draftLinks.map((link) => ({ ...link })));
  };

  const isDirty = JSON.stringify({
    draftDate,
    draftInfoDue,
    draftLinks,
  }) !== JSON.stringify(savedState || {
    draftDate: '',
    draftInfoDue: '',
    draftLinks: [emptyLink()],
  });

  const saveDraftSettings = async () => {
    setSaving(true);

    const payload = {
      draftDate: draftDate.trim(),
      draftInfoDue: draftInfoDue.trim(),
      draftLinks: draftLinks.map((link) => ({
        description: (link.description || '').trim(),
        link: (link.link || '').trim(),
        icon: link.icon || 'football',
      })),
    };

    const result = await setDraftDetails(payload);

    if (result.success) {
      const nextSavedState = {
        draftDate: payload.draftDate,
        draftInfoDue: payload.draftInfoDue,
        draftLinks: payload.draftLinks.map((link) => ({ ...link })),
      };

      setSavedState(nextSavedState);
      toastSuccess('Draft settings saved');
    } else {
      toastError(result.message || 'Unable to save draft settings');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <FontAwesomeIcon icon={faSpinner} size="2x" spin className="text-yellow" />
      </div>
    );
  }

  return (
    <div className="draft-settings-manager">
      <div className="row">
        <div className="col-12 col-md-6 mb-3">
          <label className="label text-yellow-light" htmlFor="draft-settings-date">Draft Date</label>
          <input
            id="draft-settings-date"
            type="text"
            className="form-control"
            value={draftDate}
            onChange={(event) => setDraftDate(event.target.value)}
            placeholder="e.g. August 24, 2027 7:00 PM"
          />
        </div>

        <div className="col-12 col-md-6 mb-3">
          <label className="label text-yellow-light" htmlFor="draft-settings-info-due">Draft Info Due</label>
          <input
            id="draft-settings-info-due"
            type="text"
            className="form-control"
            value={draftInfoDue}
            onChange={(event) => setDraftInfoDue(event.target.value)}
            placeholder="e.g. August 20, 2027 11:59 PM"
          />
        </div>
      </div>

      <div className="draft-settings-links mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 text-yellow-light">Draft Links</h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-success text-green-alt px-3 shadow"
            onClick={addDraftLink}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Draft Link
          </button>
        </div>

        {draftLinks.map((link, index) => (
          <div key={`draft-link-${index}`} className="draft-settings-link-item shadow p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <strong className="text-yellow-light">Draft Link {index + 1}</strong>
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-yellow-light p-0 mr-2"
                  onClick={() => moveDraftLink(index, index - 1)}
                  disabled={index === 0}
                  aria-label={`Move draft link ${index + 1} up`}
                  title="Move up"
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm text-yellow-light p-0 mr-2"
                  onClick={() => moveDraftLink(index, index + 1)}
                  disabled={index === draftLinks.length - 1}
                  aria-label={`Move draft link ${index + 1} down`}
                  title="Move down"
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </button>
                {draftLinks.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    onClick={() => removeDraftLink(index)}
                    aria-label={`Remove draft link ${index + 1}`}
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-4 mb-3">
                <label className="label text-yellow-light" htmlFor={`draft-link-description-${index}`}>Description</label>
                <input
                  id={`draft-link-description-${index}`}
                  type="text"
                  className="form-control"
                  value={link.description}
                  onChange={(event) => updateLink(index, 'description', event.target.value)}
                  placeholder="League Zoom"
                />
              </div>

              <div className="col-12 col-md-5 mb-3">
                <label className="label text-yellow-light" htmlFor={`draft-link-url-${index}`}>URL</label>
                <input
                  id={`draft-link-url-${index}`}
                  type="url"
                  className="form-control"
                  value={link.link}
                  onChange={(event) => updateLink(index, 'link', event.target.value)}
                  placeholder="https://example.com"
                />
              </div>

              <div className="col-12 col-md-3 mb-3">
                <label className="label text-yellow-light" htmlFor={`draft-link-icon-${index}`}>Icon</label>
                <select
                  id={`draft-link-icon-${index}`}
                  className="form-control"
                  value={link.icon || 'football'}
                  onChange={(event) => updateLink(index, 'icon', event.target.value)}
                >
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex align-items-center">
        <button
          type="button"
          className="btn btn-success btn-sm px-3 shadow mr-2"
          onClick={saveDraftSettings}
          disabled={saving || !isDirty}
        >
          {saving
            ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            : <FontAwesomeIcon icon={faSave} className="mr-2" />}
          Save Settings
        </button>

        <button
          type="button"
          className="btn btn-outline-light btn-sm px-3 shadow"
          onClick={resetDraftSettings}
          disabled={!isDirty}
        >
          <FontAwesomeIcon icon={faUndo} className="mr-2" />
          Reset
        </button>
      </div>
    </div>
  );
};

export default DraftSettingsManager;
